"use client"
import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment } from "@opensea/graphql"
import {
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandItemContent,
  CommandItemDescription,
  CommandItemSide,
  CommandItemTitle,
  CommandList,
  Flex,
  Tooltip,
} from "@opensea/ui-kit"
import { useTranslations } from "next-intl"
import { useRef } from "react"
import { useTraitsQueryParam } from "../../hooks/query-param/use-traits-query-param"
import { HIGH_ITEM_COUNT_THRESHOLD } from "./constants"

export const TraitFiltersStringTraitInputFragment = graphql(`
  fragment TraitFiltersStringTraitInputFragment on CollectionAttribute {
    traitType
    values {
      value
      count
    }
  }
`)

type BaseTraitFilterStringTraitInputProps = {
  traitType: string
  values: Array<{
    value: string
    count: number
  }>
  traits: Array<{
    traitType: string
    values: string[]
  }>
  onSelectTrait: (traitType: string, value: string) => void
  disableHighItemCounts?: boolean
}

export function BaseTraitFilterStringTraitInput({
  traitType,
  values,
  traits,
  onSelectTrait,
  disableHighItemCounts,
}: BaseTraitFilterStringTraitInputProps) {
  const t = useTranslations("TraitFiltersStringTraitInput")
  const listRef = useRef<HTMLDivElement>(null)
  const matchedTraitType = traits.find(v => v.traitType === traitType)

  return (
    <Command>
      <Flex>
        <CommandInput
          onValueChange={() => {
            // NOTE: Command should do this automatically but seems bugged. If this happens in another instance, patch this in DS.
            setTimeout(() => {
              listRef.current?.scrollTo({ top: 0 })
            }, 0)
          }}
          placeholder={t("placeholder")}
          size="sm"
        />
      </Flex>
      <CommandList className="scrollbar-hidden [&_[cmdk-list-sizer]]:flex [&_[cmdk-list-sizer]]:flex-col [&_[cmdk-list-sizer]]:gap-1.5">
        <CommandEmpty>{t("noResultsFound")}</CommandEmpty>
        <CommandGroup className="max-h-[205px] overflow-y-scroll" ref={listRef}>
          {values.map(trait => {
            const itemCountThresholdExceeded =
              disableHighItemCounts && trait.count > HIGH_ITEM_COUNT_THRESHOLD

            const onClick = () => {
              if (!itemCountThresholdExceeded) {
                onSelectTrait(traitType, trait.value)
              }
            }
            return (
              <Tooltip
                content={t("countExceededError")}
                disabled={!itemCountThresholdExceeded}
                key={trait.value}
              >
                <CommandItem
                  className="gap-2 px-2 py-2.5"
                  onSelect={onClick}
                  value={trait.value}
                >
                  <Checkbox
                    checked={matchedTraitType?.values.includes(trait.value)}
                    disabled={itemCountThresholdExceeded}
                  />
                  <CommandItemContent>
                    <CommandItemTitle
                      color={
                        itemCountThresholdExceeded
                          ? "text-tertiary"
                          : "text-secondary"
                      }
                      size="sm"
                      weight="regular"
                    >
                      {trait.value}
                    </CommandItemTitle>
                  </CommandItemContent>
                  <CommandItemSide>
                    <CommandItemDescription
                      color={
                        itemCountThresholdExceeded ? "text-tertiary" : undefined
                      }
                    >
                      {trait.count}
                    </CommandItemDescription>
                  </CommandItemSide>
                </CommandItem>
              </Tooltip>
            )
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}

type Props = {
  attribute: FragmentOf<typeof TraitFiltersStringTraitInputFragment>
  disableHighItemCounts?: boolean
}

export function TraitFiltersStringTraitInput({
  attribute: attributeFragment,
  disableHighItemCounts,
}: Props) {
  const attribute = readFragment(
    TraitFiltersStringTraitInputFragment,
    attributeFragment,
  )
  const { traits, onSelectTrait } = useTraitsQueryParam()

  return (
    <BaseTraitFilterStringTraitInput
      disableHighItemCounts={disableHighItemCounts}
      onSelectTrait={onSelectTrait}
      traits={traits}
      traitType={attribute.traitType}
      values={attribute.values}
    />
  )
}
